import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ApiError } from '~/api/errors';
import { GoogleLogo } from '~/components/logos/GoogleLogo';
import { LilyLogo } from '~/components/logos/LilyLogo';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Separator } from '~/components/ui/separator';
import { PRIVACY_POLICY_URL } from '~/constants/config/constants';
import { ENV } from '~/constants/config/env';
import { fetchUserAfterLogin } from '~/features/auth/services/auth.service';
import {
  resolvePostLoginRedirect,
  stashPostLoginRedirect,
} from '~/features/auth/utils/post-login-redirect';
import {
  buildBridgeUrl,
  clearBridgeReturn,
  readBridgeParams,
  shouldBridgeSso,
  stashBridgeReturn,
} from '~/features/auth/utils/sso-bridge';
import { wakeServices } from '~/features/integrations/services/health.service';
import { useAuthStore } from '~/store/auth.store';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const ssoBaseUrl = import.meta.env.DEV ? '/api' : '/api/v1/identity-service';

const SSO_PROVIDERS = [
  {
    key: 'google' as const,
    enabled: ENV.VITE_SSO_GOOGLE_ENABLED,
    href: `${ssoBaseUrl}/auth/google?redirect_origin=${encodeURIComponent(window.location.origin)}`,
    i18nKey: 'auth:login.googleButton',
    icon: GoogleLogo,
  },
] as const;

/** Trusted-by ticker logos. Self-hosted under `public/assets/images/brands/`
 *  (the lily.ai WordPress uploads they used to load from are gone). Order matches
 *  the approved brand-strip layout, read left-to-right, top-to-bottom. */
const BRAND_LOGOS = [
  { name: 'Coach', src: '/assets/images/brands/coach.webp' },
  { name: 'M&S', src: '/assets/images/brands/ms.webp' },
  { name: 'NARS', src: '/assets/images/brands/nars.webp' },
  { name: 'Shiseido', src: '/assets/images/brands/shiseido.webp' },
  { name: 'Vuori', src: '/assets/images/brands/vuori.webp' },
  { name: 'Fabletics', src: '/assets/images/brands/fabletics.webp' },
  { name: 'Beyond Yoga', src: '/assets/images/brands/beyond-yoga.webp' },
  { name: 'J.Crew', src: '/assets/images/brands/jcrew.webp' },
  { name: 'Simkhai', src: '/assets/images/brands/simkhai.webp' },
  { name: 'HOKA', src: '/assets/images/brands/hoka.webp' },
  { name: 'Tecovas', src: '/assets/images/brands/tecovas.webp' },
  { name: 'Foot Locker', src: '/assets/images/brands/foot-locker.webp' },
  { name: 'Kate Spade', src: '/assets/images/brands/kate-spade.webp' },
  { name: 'UGG', src: '/assets/images/brands/ugg.webp' },
  { name: 'Hibbett Sports', src: '/assets/images/brands/hibbett.webp' },
  { name: 'Clé de Peau Beauté', src: '/assets/images/brands/cle-de-peau.webp' },
  { name: 'Bombas', src: '/assets/images/brands/bombas-mark.webp' },
  { name: 'J.McLaughlin', src: '/assets/images/brands/jmclaughlin.webp' },
  { name: 'Arhaus', src: '/assets/images/brands/arhaus.webp' },
  { name: 'Drunk Elephant', src: '/assets/images/brands/drunk-elephant.webp' },
];

export function LoginPage() {
  const { t } = useTranslation('auth');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  // `redirect` is set by requireAuth when an unauthenticated user hits a
  // protected URL — we honour it after login so deep-links land where the
  // user originally asked. `bridgeFor` + `bridgeProvider` flag this load as
  // the SSO-bridge entry for a localhost dev session — see sso-bridge.ts.
  const { redirect, bridgeFor, bridgeProvider, error: searchError } = useSearch({ from: '/login' });
  // An SSO/callback failure arrives as an `?error=<code>` param (e.g. the
  // identity-service callback redirecting back on a token-exchange failure).
  // Map known codes to a friendly message; anything else falls back to a
  // generic one so a raw backend code never surfaces to the user.
  const [loginError, setLoginError] = useState<string | null>(() => {
    if (!searchError) return null;
    const key =
      searchError === 'oauth_failed'
        ? 'login.ssoError.oauthFailed'
        : searchError === 'login_required'
          ? 'login.ssoError.loginRequired'
          : searchError === 'interaction_required'
            ? 'login.ssoError.interactionRequired'
            : 'login.ssoError.generic';
    return t(key);
  });
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  // Bridge mode: this load was triggered by a localhost dev session asking
  // us to initiate SSO on its behalf. Skip the normal auto-redirect so we
  // can run the bridge effect below, which will leave the page anyway.
  const isBridge = Boolean(bridgeFor && bridgeProvider);

  useEffect(() => {
    if (isBridge) return;
    if (!isAuthenticated) return;
    // Already-authenticated visitors hitting /login still get the deep-link
    // honoured (e.g. they used the back button). Use window.location.replace
    // so the redirect is a clean browser navigation — avoids any TanStack
    // Router state issues when the destination contains dynamic path segments.
    void resolvePostLoginRedirect(redirect).then((dest) => {
      window.location.replace(dest);
    });
  }, [isAuthenticated, redirect, isBridge]);

  // SSO-bridge entry: when /login receives `bridgeFor` + `bridgeProvider`,
  // stash the localhost return URL on this origin and immediately fire the
  // matching SSO provider. The deployed FE's origin is in the backend's
  // ALLOWED_ORIGINS, so this hop is what makes the post-OAuth redirect
  // come back to us instead of landing on the cloud FE's home page.
  // If the bridge can't proceed (invalid params, provider disabled on this
  // FE), drop the bridge query params and fall back to the regular form.
  useEffect(() => {
    if (!isBridge) return;
    const bridge = readBridgeParams(bridgeFor, bridgeProvider);
    if (!bridge) {
      void navigate({ to: '/login' });
      return;
    }
    const provider = SSO_PROVIDERS.find((p) => p.key === bridge.provider);
    if (!provider?.enabled) {
      void navigate({ to: '/login' });
      return;
    }
    stashBridgeReturn(bridge.returnTo);
    window.location.href = provider.href;
  }, [isBridge, bridgeFor, bridgeProvider, navigate]);

  // Drop any abandoned bridge stash from a prior incomplete flow when
  // /login is loaded without bridge params — otherwise a stale stash could
  // forward a fresh non-bridge OAuth callback to a localhost that may no
  // longer be running.
  useEffect(() => {
    if (!isBridge) clearBridgeReturn();
  }, [isBridge]);

  const onSubmit = form.handleSubmit(async (data) => {
    setLoginError(null);
    try {
      const user = await fetchUserAfterLogin(data.email, data.password);
      setUser(user);
      // Warm every backend service (min-instances=0 → 1) so the first real
      // action after login doesn't pay the cold-start cost. Fire-and-forget —
      // never blocks or fails the login.
      wakeServices();
      const dest = await resolvePostLoginRedirect(redirect);
      void navigate({ to: dest });
    } catch (err) {
      // Use the server's message when available (e.g. "Please verify your email address…"),
      // otherwise fall back to the generic credentials error.
      const message = err instanceof ApiError ? err.message : null;
      setLoginError(message ?? t('login.invalidCredentials'));
    }
  });

  const enabledProviders = SSO_PROVIDERS.filter((p) => p.enabled);

  // Show a loading state instead of the form while the bridge effect
  // navigates away to the SSO provider — otherwise the cloud FE's login
  // form flashes for a beat before the redirect fires.
  if (isBridge) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-muted-foreground">{t('login.redirectingToProvider')}</p>
      </div>
    );
  }

  // Lock the layout to viewport height so the global `html { overflow: hidden }`
  // (see styles/tailwind/base.css) can suppress the window scrollbar; the left
  // column scrolls internally as a safety net for very short viewports
  // (e.g. 1366×768 laptops, DevTools docked) so the SSO button stays reachable.
  return (
    <div className="bg-neutral-30 grid h-dvh grid-cols-1 grid-rows-1 overflow-hidden lg:grid-cols-2">
      {/* ── Left: Login form ── */}
      {/* `short:` variants compress vertical spacing when viewport height ≤ 900px
          (see styles/tailwind/tokens.css). At normal viewports the original design
          is preserved; at short viewports content compresses to fit without scroll. */}
      <div className="short:py-6 flex min-h-0 flex-col [justify-content:safe_center] overflow-y-auto px-8 py-10 lg:px-16 xl:px-24">
        <div className="flex flex-col items-center justify-start">
          <div className="w-full max-w-lg 2xl:max-w-[560px]">
            {/* Logo lives inside the max-w-lg wrapper so its left edge always
                aligns with the form's left edge, regardless of viewport width.
                Negative margin offsets the SVG's internal viewBox padding
                (~13 units / 119 wide) so the visible logo glyph — not its bounding
                box — lines up with the form. */}
            <LilyLogo className="short:h-24 short:-ml-[14px] -ml-[19px] h-32 w-auto" />
            <h1 className="text-muted-foreground short:mt-4 mt-8 text-3xl font-medium tracking-tight">
              {t('login.welcomeBack')}
            </h1>
            <p className="text-muted-foreground short:mt-3 mt-5 text-base leading-relaxed">
              {t('login.subtitle')}
              <br />
              {t('login.subtitleCta')}
            </p>

            {/* Inputs + buttons sit in a narrower max-w-sm sub-wrapper so they're
                visually only slightly wider than the subtitle text above — design intent
                is for the form to be tighter than the headers, not the same width. */}
            {/* At 2xl+ (viewport ≥ 1536), the form's width grows linearly with the
                viewport — clamp() ramps from 480 px at 1536 up to 560 px at 1696,
                then caps. Below 2xl, the static max-w-[480px] still applies. */}
            <div className="w-full max-w-[480px] 2xl:w-[clamp(480px,calc(50vw-288px),560px)] 2xl:max-w-[560px]">
              {/* Email form */}
              <form
                onSubmit={(e) => void onSubmit(e)}
                className="short:mt-6 short:space-y-4 mt-10 space-y-6"
              >
                <div>
                  <Label htmlFor="email" className="text-base">
                    {t('login.emailLabel')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('login.emailPlaceholder')}
                    autoComplete="email"
                    className="mt-4 h-14 rounded-xl text-base"
                    aria-invalid={!!form.formState.errors.email}
                    aria-describedby={loginError ? 'login-error' : undefined}
                    {...form.register('email')}
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-base">
                    {t('login.passwordLabel')}
                  </Label>
                  <div className="relative mt-4">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('login.passwordPlaceholder')}
                      autoComplete="current-password"
                      className="h-14 rounded-xl pr-12 text-base"
                      aria-invalid={!!form.formState.errors.password}
                      aria-describedby={loginError ? 'login-error' : undefined}
                      {...form.register('password')}
                    />
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer"
                      onClick={() => {
                        setShowPassword((prev) => !prev);
                      }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff aria-hidden="true" className="h-5 w-5" />
                      ) : (
                        <Eye aria-hidden="true" className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {loginError && (
                  <p id="login-error" role="alert" className="text-destructive text-sm">
                    {loginError}
                  </p>
                )}
                <Button
                  type="submit"
                  className="h-14 w-full rounded-xl text-lg"
                  disabled={form.formState.isSubmitting}
                  isLoading={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? t('login.signingIn') : t('login.signInButton')}
                </Button>
              </form>

              {/* SSO providers */}
              {enabledProviders.length > 0 && (
                <>
                  <div className="short:my-6 relative my-10">
                    <Separator />
                    <span className="bg-neutral-30 text-muted-foreground absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-sm">
                      {t('login.orContinueWith')}
                    </span>
                  </div>
                  <div className="space-y-4">
                    {enabledProviders.map((provider) => (
                      <Button
                        key={provider.key}
                        variant="outline"
                        className="bg-card h-14 w-full gap-4 rounded-xl text-lg"
                        onClick={() => {
                          // Stash the deep-link first — sessionStorage on this
                          // origin survives the cross-origin OAuth round-trip,
                          // so it works for both bridge and direct flows.
                          stashPostLoginRedirect(redirect);
                          // Localhost dev pointed at a remote API can't receive
                          // the post-OAuth redirect directly (its origin is not
                          // in the backend's ALLOWED_ORIGINS). Hand SSO to the
                          // remote FE which IS in the allowlist; it forwards
                          // the auth code back to localhost when done.
                          if (shouldBridgeSso()) {
                            const bridgeUrl = buildBridgeUrl(provider.key, window.location.origin);
                            if (bridgeUrl) {
                              window.location.href = bridgeUrl;
                              return;
                            }
                          }
                          window.location.href = provider.href;
                        }}
                      >
                        <provider.icon className="!h-8 !w-8 shrink-0" />
                        {t(provider.i18nKey)}
                      </Button>
                    ))}
                  </div>
                </>
              )}

              {/* Privacy policy — external marketing page, so a plain anchor in a new tab */}
              <p className="mt-6 text-center text-sm">
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground cursor-pointer underline-offset-4 hover:underline"
                >
                  {t('login.privacyPolicy')}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: Promotional panel ── */}
      <div className="hidden p-6 lg:block">
        {/* justify-start (not justify-center) keeps the headline + subtitle anchored
            to the top when the panel is shorter than its content (e.g. 1280×700);
            mt-auto on the trusted-by section pushes it to the bottom when there IS
            room, preserving the original spacing at larger viewports. */}
        <div className="bg-gradient-promo short:py-6 flex h-full flex-col overflow-y-auto rounded-3xl px-12 py-10 text-neutral-100">
          <div>
            <h2 className="short:text-3xl text-5xl leading-tight font-bold">
              The easiest method to handle products and materials.
            </h2>
            <p className="mt-4 text-lg opacity-80">{t('login.promoSubtitle')}</p>
          </div>

          {/* App screenshot with product card overlay */}
          {/* mb reserves non-collapsing clearance for the product card, which
              overhangs the container via translate-y-[20%]; without it the
              overhang collides with the trusted-by ticker on short viewports
              where the mt-auto gap below collapses to zero. */}
          <div className="short:mt-4 short:mb-6 relative mt-6 mb-10">
            <img
              src="/assets/images/promo-app-screenshot.png"
              alt="Lily AI Catalog Manager"
              className="w-[92%] rounded-xl shadow-2xl"
            />
            <img
              src="/assets/images/promo-product-card.png"
              alt="Example product card"
              className="absolute right-[8%] bottom-0 w-[37%] translate-x-[20%] translate-y-[20%] rounded-xl shadow-xl"
            />
          </div>

          {/* Trusted by — ticker */}
          <div className="short:pt-6 mt-auto pt-10 pb-4 text-center">
            <p className="text-sm font-normal opacity-70">{t('login.trustedBy')}</p>
            <div className="short:mt-4 relative mt-8 overflow-hidden">
              <div className="animate-ticker flex w-max items-center gap-12">
                {[...BRAND_LOGOS, ...BRAND_LOGOS, ...BRAND_LOGOS, ...BRAND_LOGOS].map(
                  (brand, i) => (
                    // Fixed cell normalises the logos' very different aspect ratios
                    // into one footprint; `grayscale`+`opacity` keep the band a
                    // cohesive soft grey on the promo gradient. All source files are
                    // transparent (the few that shipped with a baked-in white
                    // background were matte-removed), so no blend hack is needed —
                    // `mix-blend-multiply` wouldn't work here anyway, since the
                    // ticker's transform animation isolates its stacking context.
                    <div
                      key={`${brand.name}-${String(i)}`}
                      className="short:h-8 flex h-10 w-32 shrink-0 items-center justify-center"
                    >
                      <img
                        src={brand.src}
                        alt={brand.name}
                        className="max-h-full max-w-full object-contain opacity-70 grayscale"
                      />
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
